import React, { Fragment, useReducer, useRef as _useRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';

// Define initial state
const initialState = {
  isClosing: false,
  isAnimating: false
};

// Define reducer function
const reducer = (state, action) => {
  switch (action.type) {
    case 'START_CLOSING':
      return { ...state, isClosing: true };
    case 'FINISH_CLOSING':
      return { ...state, isClosing: false };
    case 'SET_ANIMATING':
      return { ...state, isAnimating: action.payload };
    default:
      return state;
  }
};

const Dialog = ({ isOpen, onClose, onAfterClose, title, children, actions, width = 'max-w-md', testId = 'dialog', initialFocus }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const widthClass = width;
  const fullWidthClass = width.includes('w-fit') ? '' : 'w-full';

  // Internal trigger for Headless UI's onClose - starts the animation
  const handleInternalCloseTrigger = () => {
    if (isOpen && !state.isClosing) { // Prevent triggering multiple times
      dispatch({ type: 'START_CLOSING' });
      onClose(); // Call onClose immediately for better responsiveness
    }
  };

  const handleOutsideMouseDown = (event) => {
    if (event.target !== event.currentTarget) return;
    handleInternalCloseTrigger();
  };

  // Animation handlers (optional, for tracking state)
  const handleAnimationStart = () => {
    dispatch({ type: 'SET_ANIMATING', payload: true });
  };
  const handleAnimationEnd = () => {
    dispatch({ type: 'SET_ANIMATING', payload: false });
  };

  // Function called AFTER the leave animation finishes
  const handleAfterLeave = () => {
    handleAnimationEnd(); // Reset animation state
    dispatch({ type: 'FINISH_CLOSING' }); // Reset internal closing state
    
    // Call the onAfterClose callback if provided
    if (onAfterClose && typeof onAfterClose === 'function') {
      onAfterClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      {/* The show prop controls the Transition based on isOpen */}
      <HeadlessDialog
        as="div"
        className="fixed inset-0 z-50"
        onClose={handleInternalCloseTrigger} // HeadlessUI triggers this on overlay click/Esc
        initialFocus={initialFocus}
        data-testid={testId}
      >
        {/* Overlay Transition */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          // No afterLeave needed here if we only care about the panel
        >
          <div className={`fixed inset-0 bg-black bg-opacity-40 dark:bg-opacity-60`} data-testid={`${testId}-overlay`} />
        </Transition.Child>

        {/* Panel Transition */}
        <div
          className="fixed inset-0 overflow-y-auto"
          data-testid={`${testId}-dismiss-area`}
          onMouseDown={handleOutsideMouseDown}
        >
          <div
            className="flex min-h-full items-center justify-center p-2 text-center"
            onMouseDown={handleOutsideMouseDown}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
              beforeEnter={handleAnimationStart} // Optional: track animation state
              afterEnter={handleAnimationEnd}  // Optional: track animation state
              beforeLeave={handleAnimationStart} // Optional: track animation state
              afterLeave={handleAfterLeave} // CRITICAL: Call external onClose here
            >
              <HeadlessDialog.Panel
                className={`${fullWidthClass} ${widthClass} transform rounded-[20px] bg-surface text-foreground border border-border-light p-3 sm:p-6 text-left align-middle shadow-card transition-all ring-2 ring-black/10 dark:bg-surface dark:ring-white/20 backdrop-blur-sm max-h-[calc(100vh-2rem)] overflow-y-auto ${state.isAnimating ? 'will-change-transform will-change-opacity' : ''}`}
                data-testid={`${testId}-panel`}
              >
                <div className="flex items-start justify-between gap-4">
                  {title && (
                    <HeadlessDialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-foreground"
                      data-testid={`${testId}-title`}
                    >
                      {title}
                    </HeadlessDialog.Title>
                  )}
                  <button
                    type="button"
                    onClick={handleInternalCloseTrigger}
                    className="flex-shrink-0 rounded-[10px] p-1.5 text-muted-foreground hover:text-foreground hover:bg-background-light transition-colors"
                    style={{ minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    data-testid={`${testId}-close-btn`}
                    aria-label="Close dialog"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                <div className="mt-2" data-testid={`${testId}-content`}>
                  {children}
                </div>

                {actions && (
                  <div className="mt-6 flex justify-end space-x-3" data-testid={`${testId}-actions`}>
                    {actions}
                  </div>
                )}
              </HeadlessDialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
};

Dialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAfterClose: PropTypes.func,
  title: PropTypes.node,
  children: PropTypes.node.isRequired,
  actions: PropTypes.node,
  width: PropTypes.string,
  testId: PropTypes.string,
  initialFocus: PropTypes.object // Ref object for initial focus
};

export default Dialog;
